import React from "react";
import { AlertTriangle, Zap, MessageSquare, CalendarClock } from "lucide-react";

const exact = (n) => (n ?? 0).toLocaleString("pt-BR");

// Formato amigável: 318.450 -> "318 mil", 1.000.000 -> "1 milhão".
const friendly = (n) => {
    n = n ?? 0;
    if (n >= 1_000_000) {
        const v = n / 1_000_000;
        const s = (Math.round(v * 10) / 10).toLocaleString("pt-BR");
        return `${s} ${v >= 2 ? "milhões" : "milhão"}`;
    }
    if (n >= 1000) return `${Math.round(n / 1000).toLocaleString("pt-BR")} mil`;
    return exact(n);
};

const fmtDate = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleDateString("pt-BR"); } catch { return "—"; }
};

const alertFor = (pct) => {
    if (pct >= 100) return { level: 100, tone: "red", msg: "Você atingiu o limite mensal de utilização do Assistente de IA do seu plano. Seu limite será renovado no próximo ciclo de assinatura." };
    if (pct >= 90) return { level: 90, tone: "red", msg: "Atenção: você já utilizou 90% da sua franquia mensal de IA." };
    if (pct >= 80) return { level: 80, tone: "amber", msg: "Você utilizou 80% da sua franquia mensal de IA. Acompanhe seu consumo para evitar a interrupção do serviço." };
    if (pct >= 70) return { level: 70, tone: "amber", msg: "Você utilizou 70% da sua franquia mensal de Inteligência Artificial." };
    return null;
};

const barColor = (pct) => (pct >= 90 ? "bg-red-500" : pct >= 70 ? "bg-amber-500" : "bg-emerald-500");

function Bar({ icon: Icon, label, used, limit, remaining, display, testid }) {
    const pct = limit ? Math.min(Math.round((used / limit) * 100), 100) : 0;
    return (
        <div data-testid={testid}>
            <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-semibold text-slate-600 flex items-center gap-1.5"><Icon className="w-3.5 h-3.5" />{label}</span>
                <span className="text-xs text-slate-500" title={`${exact(used)} / ${exact(limit)}`}>{display}</span>
            </div>
            <div className="h-2.5 rounded-full bg-slate-100 overflow-hidden">
                <div className={`h-full rounded-full transition-all ${barColor(pct)}`} style={{ width: `${pct}%` }} data-testid={`${testid}-fill`} />
            </div>
            <div className="flex items-center justify-between mt-1">
                <span className="text-[11px] text-slate-400">{pct}% utilizado</span>
                <span className="text-[11px] text-slate-500" title={exact(remaining)} data-testid={`${testid}-remaining`}>Restam <b className="text-slate-700">{display && remaining >= 1000 ? friendly(remaining) : exact(remaining)}</b></span>
            </div>
        </div>
    );
}

export default function AiUsagePanel({ usage }) {
    if (!usage) return null;
    const pct = usage.pct ?? 0;
    const alert = alertFor(pct);
    const toneCls = alert?.tone === "red" ? "bg-red-50 border-red-200 text-red-700" : "bg-amber-50 border-amber-200 text-amber-800";
    return (
        <div className="rounded-lg border border-slate-200 bg-white p-4 space-y-3" data-testid="ai-usage-panel">
            <div className="flex items-center justify-between">
                <h3 className="text-sm font-heading font-bold text-slate-800">Uso da Inteligência Artificial</h3>
                <span className="text-[11px] text-slate-500 flex items-center gap-1" data-testid="ai-usage-renewal">
                    <CalendarClock className="w-3.5 h-3.5" />Renovação {fmtDate(usage.renewal_date)}
                </span>
            </div>
            {alert && (
                <div className={`rounded-md border px-3 py-2 text-xs flex items-start gap-1.5 ${toneCls}`} data-testid="ai-usage-alert" data-alert-level={alert.level}>
                    <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />{alert.msg}
                </div>
            )}
            <Bar icon={MessageSquare} label="Consultas" testid="ai-usage-queries"
                used={usage.queries_used} limit={usage.query_limit} remaining={usage.queries_remaining ?? Math.max((usage.query_limit || 0) - (usage.queries_used || 0), 0)}
                display={`${exact(usage.queries_used)} / ${exact(usage.query_limit)}`} />
            <Bar icon={Zap} label="Tokens" testid="ai-usage-tokens"
                used={usage.tokens_used} limit={usage.token_limit} remaining={usage.tokens_remaining ?? Math.max((usage.token_limit || 0) - (usage.tokens_used || 0), 0)}
                display={`${friendly(usage.tokens_used)} / ${friendly(usage.token_limit)}`} />
        </div>
    );
}
